import { api } from './client';

// ── Posting slots ───────────────────────────────────────────────────────────────

export interface PostingSlot {
  id: string;
  pinterestAccountId: string;
  weekday: number; // 0 = Sunday … 6 = Saturday
  minuteOfDay: number; // 0–1439, wall-clock in `timezone`
  timezone: string;
  createdAt: string;
}

export function listSlots(accountId: string, token: string | null): Promise<PostingSlot[]> {
  return api.get<PostingSlot[]>(`/scheduler/accounts/${accountId}/slots`, token);
}

export function createSlot(
  accountId: string,
  input: { weekday: number; minuteOfDay: number; timezone: string },
  token: string | null,
): Promise<PostingSlot> {
  return api.post<PostingSlot>(`/scheduler/accounts/${accountId}/slots`, input, token);
}

export function deleteSlot(slotId: string, token: string | null): Promise<void> {
  return api.delete(`/scheduler/slots/${slotId}`, token);
}

// ── Scheduled pins ────────────────────────────────────────────────────────────────

export type ScheduledPinStatus = 'scheduled' | 'publishing' | 'published' | 'failed';

export interface ScheduledPin {
  id: string;
  pinterestAccountId: string;
  pinterestBoardId: string;
  title: string;
  description: string | null;
  link: string | null;
  altText: string | null;
  imageUrl: string;
  scheduledFor: string; // ISO UTC
  timezone: string;
  status: ScheduledPinStatus;
  publishedPinId: string | null;
  lastError: string | null;
  attempts: number;
  /** Keyword this pin should rank for; drives the post-publish rank loop. */
  targetKeyword: string | null;
  /** RankTracker created on publish (present once published with a targetKeyword). */
  rankTrackerId: string | null;
  createdAt: string;
}

export function listScheduledPins(
  filter: { accountId?: string; status?: ScheduledPinStatus },
  token: string | null,
): Promise<ScheduledPin[]> {
  const params = new URLSearchParams();
  if (filter.accountId) params.set('accountId', filter.accountId);
  if (filter.status) params.set('status', filter.status);
  const qs = params.toString();
  return api.get<ScheduledPin[]>(`/scheduler/pins${qs ? `?${qs}` : ''}`, token);
}

export interface SchedulePinInput {
  pinterestAccountId: string;
  pinterestBoardId: string;
  title: string;
  description?: string;
  link?: string;
  altText?: string;
  imageUrl: string;
  /** Omit to auto-assign the next open posting slot ("add to queue"). */
  scheduledFor?: string; // ISO UTC
  timezone: string;
  /** Keyword to rank-track once published. */
  targetKeyword?: string;
}

export function schedulePin(
  input: SchedulePinInput,
  token: string | null,
): Promise<ScheduledPin> {
  return api.post<ScheduledPin>('/scheduler/pins', input, token);
}

export function reschedulePin(
  id: string,
  scheduledFor: string,
  token: string | null,
): Promise<ScheduledPin> {
  return api.patch<ScheduledPin>(`/scheduler/pins/${id}`, { scheduledFor }, token);
}

export function cancelScheduledPin(id: string, token: string | null): Promise<void> {
  return api.delete(`/scheduler/pins/${id}`, token);
}

// ── Boards (from account-explorer) ────────────────────────────────────────────────

export interface Board {
  id: string;
  pinterestBoardId: string;
  name: string;
  privacy: string;
  pinCount: number;
}

export function listBoards(accountId: string, token: string | null): Promise<Board[]> {
  return api.get<Board[]>(`/account-explorer/accounts/${accountId}/boards`, token);
}
